import { Button, IconNode, Modal, Typography, Loader } from '@components/base';
import Images from '@assets/images';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { addOperationCosts as addOperationCostsService } from '@services/task.service';
import notify from '@helpers/toastify-helper';
import { CURRENCY, TRANSPORTATION_MODE_ENUM } from '@helpers/constants';
import { ISelect } from '@helpers/types';
import css from './index.module.scss';
import QuotePricePackageWrapper from '@components/common/quote-wrapper/components/quote-price-package-wrapper';
import { addOperationCostsAirSea, addOperationCostsRoadRail } from '@helpers/yup/add-vessel.schema';

interface Props {
  open: boolean;
  onClose?: () => void;
  sendStatusToCard: (arg: boolean) => void;
  taskId: string;
  onComplete?: () => void;
  modeOfTransport?: string;
}

interface FormValues {
  freight_expenses?: string;
  port_expenses?: string;
  freight_expenses_currency?: ISelect | null;
  port_expenses_currency?: ISelect | null;
  transportation_costs?: string;
  transportation_costs_currency?: ISelect | null;
  other_costs?: string;
  other_costs_currency?: ISelect | null;
}

type Mode = 'AIR' | 'SEA' | 'ROAD' | 'RAIL';

const CONFIG: Record<
  Mode,
  {
    schema: any;
    defaultValues: Partial<FormValues>;
    fieldGroups: Array<{
      amountKey: keyof FormValues;
      currencyKey: keyof FormValues;
      label: string;
      amountPlaceholder: string;
    }>;
  }
> = {
  AIR: {
    schema: addOperationCostsAirSea,
    defaultValues: {
      freight_expenses: '',
      port_expenses: '',
      other_costs: '',
      freight_expenses_currency: null,
      port_expenses_currency: null,
      other_costs_currency: null
    },
    fieldGroups: [
      {
        amountKey: 'freight_expenses',
        currencyKey: 'freight_expenses_currency',
        label: 'Freight Expenses',
        amountPlaceholder: 'Enter Expenses'
      },
      {
        amountKey: 'port_expenses',
        currencyKey: 'port_expenses_currency',
        label: 'Port Expenses',
        amountPlaceholder: 'Enter Expenses'
      },
      {
        amountKey: 'other_costs',
        currencyKey: 'other_costs_currency',
        label: 'Other Costs',
        amountPlaceholder: 'Enter Costs'
      }
    ]
  },
  SEA: {
    schema: addOperationCostsAirSea,
    defaultValues: {
      freight_expenses: '',
      port_expenses: '',
      other_costs: '',
      freight_expenses_currency: null,
      port_expenses_currency: null,
      other_costs_currency: null
    },
    fieldGroups: [
      {
        amountKey: 'freight_expenses',
        currencyKey: 'freight_expenses_currency',
        label: 'Freight Expenses',
        amountPlaceholder: 'Enter Expenses'
      },
      {
        amountKey: 'port_expenses',
        currencyKey: 'port_expenses_currency',
        label: 'Port Expenses',
        amountPlaceholder: 'Enter Expenses'
      },
      {
        amountKey: 'other_costs',
        currencyKey: 'other_costs_currency',
        label: 'Other Costs',
        amountPlaceholder: 'Enter Costs'
      }
    ]
  },
  ROAD: {
    schema: addOperationCostsRoadRail,
    defaultValues: {
      transportation_costs: '',
      other_costs: '',
      transportation_costs_currency: null,
      other_costs_currency: null
    },
    fieldGroups: [
      {
        amountKey: 'transportation_costs',
        currencyKey: 'transportation_costs_currency',
        label: 'Transportation Cost',
        amountPlaceholder: 'Enter Cost'
      },
      {
        amountKey: 'other_costs',
        currencyKey: 'other_costs_currency',
        label: 'Other Costs',
        amountPlaceholder: 'Enter Costs'
      }
    ]
  },
  RAIL: {
    schema: addOperationCostsRoadRail,
    defaultValues: {
      transportation_costs: '',
      other_costs: '',
      transportation_costs_currency: null,
      other_costs_currency: null
    },
    fieldGroups: [
      {
        amountKey: 'transportation_costs',
        currencyKey: 'transportation_costs_currency',
        label: 'Transportation Cost',
        amountPlaceholder: 'Enter Cost'
      },
      {
        amountKey: 'other_costs',
        currencyKey: 'other_costs_currency',
        label: 'Other Costs',
        amountPlaceholder: 'Enter Costs'
      }
    ]
  }
};

const AddOperationCostsCard = ({
  open,
  onClose,
  sendStatusToCard,
  taskId,
  onComplete,
  modeOfTransport
}: Props) => {
  const mode = modeOfTransport as Mode;

  const { schema, defaultValues, fieldGroups } = CONFIG[mode] ?? CONFIG.SEA;
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const body: Record<string, any> = {
      other_costs: data.other_costs,
      other_costs_currency: data.other_costs_currency?.value
    };

    if (mode === TRANSPORTATION_MODE_ENUM.AIR || mode === TRANSPORTATION_MODE_ENUM.SEA) {
      body.freight_expenses = data.freight_expenses;
      body.port_expenses = data.port_expenses;
      body.freight_expenses_currency = data.freight_expenses_currency?.value;
      body.port_expenses_currency = data.port_expenses_currency?.value;
    } else {
      body.transportation_cost = data.transportation_costs;
      body.transportation_cost_currency = data.transportation_costs_currency?.value;
    }

    const res = await addOperationCostsService(taskId, body);

    if (res.success) {
      notify({ message: 'Details added successfully' });
      sendStatusToCard(true);
      onComplete?.();
      onClose?.();
    } else {
      notify({
        message: res.error ?? 'Some error occurred',
        severity: 'error'
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={css.mainWrapper}>
        <div className={css.headerWrapper}>
          <Typography variant="h4">Add Operation Costs</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="div"
            className={css.closeButton}
            onClick={onClose}
          />
        </div>

        <form noValidate className={css.formWrapper} onSubmit={handleSubmit(onSubmit)}>
          <div className={css.fieldsWrapper}>
            {fieldGroups.map(({ amountKey, currencyKey, label, amountPlaceholder }) => (
              <div className={css.rowWrapper} key={String(amountKey)}>
                <QuotePricePackageWrapper
                  label={label}
                  control={control}
                  input={amountKey}
                  inputRequired={true}
                  inputPlaceholder="Enter Price"
                  currency={currencyKey}
                  currencyOptions={CURRENCY}
                  currencyPlaceholder="Currency"
                />
              </div>
            ))}
          </div>
          <div className={css.actionButtonWrapper}>
            <Button
              variant="outlined-secondary"
              className={css.actionButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className={css.actionButton} disabled={isSubmitting}>
              Save
            </Button>
          </div>
        </form>
        <Loader open={isSubmitting} />
      </div>
    </Modal>
  );
};

export default AddOperationCostsCard;
